import type React from "react";
import { useEffect } from "react";
import "./TrendingCreators.css";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Award } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import {
  getTrendingCreators,
  selectTrendingCreators,
  selectTrendingCreatorsLoading,
} from "../../store/discoverSlice";
import { toggleFollowCreator } from "../../store/dreamworldSlice";
import LoadingIndicator from "../ui/LoadingIndicator";

const TrendingCreators: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const userGlobalData = useSelector(
    (state: RootState) => state.dreamworld.userGlobalData
  );
  const creators = useSelector(selectTrendingCreators);
  const loading = useSelector(selectTrendingCreatorsLoading);

  useEffect(() => {
    if (creators.length === 0) {
      dispatch(getTrendingCreators());
    }
  }, [creators.length, dispatch]);

  return (
    <div className="horizontal-scroll-container">
      {loading === "pending" ? (
        <div className="flex items-center justify-center w-full p-6">
          <LoadingIndicator />
        </div>
      ) : creators.length === 0 ? (
        <div className="horizontal-scroll-item bg-gray-800/60 rounded-xl p-6 border border-gray-700/50 text-center w-48">
          <h3 className="text-white font-semibold text-sm mb-2">
            No Creators Found
          </h3>
          <p className="text-gray-400 text-xs">
            Check back later for trending creators.
          </p>
        </div>
      ) : (
        creators.map((creator, index) => (
          <div
            style={{
              marginLeft: index === 0 ? "8px" : undefined,
              marginRight: index === creators.length - 1 ? "8px" : undefined,
            }}
            key={creator.uid}
          >
            <Link to={`/profile/${creator.uid}`}>
              <motion.div
                className="horizontal-scroll-item bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-xl p-4 border border-gray-700/50 hover:border-purple-500/30 transition-all cursor-pointer text-center group"
                style={{ width: "150px" }}
              >
                <div className="relative inline-block mb-3">
                  <img
                    src={
                      creator.avatarUrl ||
                      "https://plus.unsplash.com/premium_photo-1673697239981-389164b7b87f?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8bmF0dXJlfGVufDB8fDB8fHww"
                    }
                    alt={creator.username}
                    className="w-16 h-16 rounded-full mx-auto object-cover ring-2 ring-purple-500/30 group-hover:ring-purple-500/60 transition-all"
                  />
                  {true && (
                    <div className="absolute -top-1 -right-1 bg-blue-500 rounded-full p-0.5">
                      <Award className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
                <h3 className="text-white font-semibold text-sm mb-1">
                  {creator.username}
                </h3>
                <p className="text-gray-400 text-xs mb-2">
                  {creator.effectsCreatedCount || 0} effects
                </p>
                {userGlobalData?.user?.uid !== creator.uid && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={(e) => {
                      e.preventDefault();
                      if (userGlobalData) {
                        dispatch(
                          toggleFollowCreator({
                            currentUserId: userGlobalData.user.uid,
                            creatorId: creator.uid,
                          })
                        );
                      }
                    }}
                    className={`w-full py-1.5 rounded-md text-sm font-medium transition-colors ${
                      userGlobalData?.userData?.following?.includes(creator.uid)
                        ? "bg-purple-600 text-white"
                        : "bg-gray-700 text-white hover:bg-purple-600"
                    }`}
                  >
                    {userGlobalData?.userData?.following?.includes(creator.uid)
                      ? "Following"
                      : "Follow"}
                  </motion.button>
                )}
              </motion.div>
            </Link>
          </div>
        ))
      )}
    </div>
  );
};

export default TrendingCreators;
